import { format } from 'date-fns';
import React, { useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Agenda } from 'react-native-calendars';
import styled from 'styled-components/native';
import Button from '../../components/Button';
import { B, P } from '../../components/Typography';
import Colors from '../../constants/Colors';

const StyledContainer = styled.View`
  align-items: center;
  flex: 1;
  padding: 10px;
  background: ${Colors.background};
`;

const StyledView = styled.View`
  width: 100%;
  flex: 1;
  background-color: ${Colors.background};
  margin-top: ${Platform.OS === 'web' ? 100 : 20}px;
  max-width: 768px;
`;

const StyledToggleContainer = styled.View`
  flex-direction: row;
`;

const StyledButtonView = styled.View`
  flex: 1;
  margin: 0 10px 0 10px;
`;

const StyledCurrentMonthView = styled.View`
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 5px;
`;

const StyledMonthText = styled(B)`
  font-size: 26px;
  line-height: 30px;
  letter-spacing: -0.77px;
`;

const StyledAgendaContainer = styled.View`
  flex: 1;
`;

const StyledAgendaItemContainer = styled.View`
  background-color: white;
  border-radius: 5px;
  padding: 10px;
  margin-right: 25px;
  margin-top: 15px;
`;

const StyledAgendaItemEmptyContainer = styled.View`
  flex: 1;
  align-items: center;
  margin-top: 150px;
`;

enum CalendarMode {
  Month,
  // Week,
  Day
}

function formatDateString(date: any): string {
  const formattedDate = format(new Date(date), 'MMMM yyyy');
  return formattedDate;
}

function generateSampleData(): {} {
  const items: any = {};
  for (let i = -2; i < 3; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    const dateKey = format(date, 'yyyy-MM-dd');
    items[dateKey] = [
      { name: 'Lorem ipsum', time: '10:00 am' },
      { name: 'Lorem ipsum', time: '1:00 pm' },
      { name: 'Lorem ipsum', time: '3:00 pm' }
    ];
  }
  return items;
}

const CalendarScreen: React.FC = () => {
  const agenda = useRef<Agenda>(null);
  const [calendarMode, setCalendarMode] = useState(CalendarMode.Day);
  const [monthText, setMonthText] = useState(formatDateString(new Date()));

  function updateMonthHeader(day: any) {
    setMonthText(`${formatDateString(day.dateString)}`);
  }

  function renderItem(item: any): any {
    return (
      <StyledAgendaItemContainer>
        <P color='dark'>{item.time}</P>
        <B color='dark'>{item.name}</B>
      </StyledAgendaItemContainer>
    );
  }

  function renderEmptyDate(): any {
    return (
      <StyledAgendaItemEmptyContainer>
        <B>No activities for this day.</B>
      </StyledAgendaItemEmptyContainer>
    );
  }

  return (
    <StyledContainer>
      <StyledView>
        <StyledToggleContainer>
          <StyledButtonView>
            <Button
              outlined={calendarMode === CalendarMode.Month}
              onPress={() => {
                if (calendarMode === CalendarMode.Month) return;
                agenda?.current?.toggleCalendarPosition(true);
                setCalendarMode(CalendarMode.Month);
              }}
            >
              Month
            </Button>
          </StyledButtonView>
          <StyledButtonView>
            <Button
              outlined={calendarMode === CalendarMode.Day}
              onPress={() => {
                if (calendarMode === CalendarMode.Day) return;
                agenda?.current?.toggleCalendarPosition(false);
                setCalendarMode(CalendarMode.Day);
              }}
            >
              Day
            </Button>
          </StyledButtonView>
        </StyledToggleContainer>
        <StyledCurrentMonthView>
          <StyledMonthText>
            {calendarMode === CalendarMode.Day ? monthText : 'Select a date'}
          </StyledMonthText>
        </StyledCurrentMonthView>
        <StyledAgendaContainer>
          <Agenda
            ref={agenda}
            items={generateSampleData()}
            onDayPress={(date: Date) => {
              updateMonthHeader(date);
              setCalendarMode(CalendarMode.Day);
            }}
            renderEmptyData={renderEmptyDate.bind(this)}
            renderItem={renderItem.bind(this)}
            theme={{
              calendarBackground: Colors.primary,
              monthTextColor: Colors.primaryPurple,
              agendaKnobColor: Colors.primaryPurple,
              dayTextColor: Colors.primaryPurple,
              dotColor: Colors.primaryPurple,
              selectedDayTextColor: Colors.primary,
              selectedDayBackgroundColor: Colors.primaryPurple,
              selectedDotColor: Colors.primary,
              textDayFontFamily: 'DMSans-Bold',
              textDayHeaderFontFamily: 'DMSans-Bold',
              textMonthFontFamily: 'DMSans-Bold',
              textDayFontSize: 12,
              todayTextColor: Colors.primaryPurple,
              todayBackgroundColor: Colors.profileMenuBackground,
              agendaDayNumColor: Colors.primaryPurple,
              agendaDayTextColor: Colors.primaryPurple,
              agendaTodayColor: Colors.primaryPurple,
              backgroundColor: Colors.primary,
              indicatorColor: Colors.primaryPurple
            }}
            hideKnob={true}
            showOnlySelectedDayItems={true}
          />
        </StyledAgendaContainer>
      </StyledView>
    </StyledContainer>
  );
};

export default CalendarScreen;
